import { useCallback, useMemo } from 'react';

import { showInfoAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';

import {
  useGuardianPlayerDetailQuery,
  useGuardianPlayerSessionSummaryQuery,
} from '@/features/guardian-player/queries';
import {
  buildPlayerDetailStats,
  calculateAge,
  calculatePlayerProgress,
} from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';

export function usePlayerDetail(initialPlayer: GuardianPlayer) {
  const { data: serverPlayer, refetch: refetchPlayerDetail } = useGuardianPlayerDetailQuery(
    initialPlayer.id,
  );

  const player = useMemo((): GuardianPlayer => {
    if (!serverPlayer) return initialPlayer;

    let dobString = initialPlayer.dob;
    if (serverPlayer.dob && typeof serverPlayer.dob === 'object' && 'seconds' in serverPlayer.dob) {
      dobString = new Date(serverPlayer.dob.seconds * 1000).toISOString();
    }

    // `GuardianPlayerDetail.createdOn` may arrive as a Firestore timestamp; normalise it to the
    // ISO string that `GuardianPlayer.createdOn` expects.
    let createdOnString = initialPlayer.createdOn;
    if (
      serverPlayer.createdOn &&
      typeof serverPlayer.createdOn === 'object' &&
      'seconds' in serverPlayer.createdOn
    ) {
      createdOnString = new Date(serverPlayer.createdOn.seconds * 1000).toISOString();
    } else if (typeof serverPlayer.createdOn === 'string') {
      createdOnString = serverPlayer.createdOn;
    }

    return {
      ...initialPlayer,
      ...serverPlayer,
      createdOn: createdOnString,
      firstname: serverPlayer.firstname ?? initialPlayer.firstname,
      lastname: serverPlayer.lastname ?? initialPlayer.lastname,
      avatarUrl:
        serverPlayer.avatar_url ??
        serverPlayer.photo_url ??
        serverPlayer.image_url ??
        initialPlayer.avatarUrl,
      level:
        serverPlayer.level !== undefined && serverPlayer.level !== null
          ? Number(serverPlayer.level)
          : initialPlayer.level,
      dob: dobString,
      progress: (serverPlayer.progress as GuardianPlayer['progress']) ?? initialPlayer.progress,
      notes: (serverPlayer.notes as GuardianPlayer['notes']) ?? initialPlayer.notes,
      numberGuardian: serverPlayer.numberGuardian ?? initialPlayer.numberGuardian,
    };
  }, [initialPlayer, serverPlayer]);

  const { data: sessionSummary, refetch: refetchSessionSummary } =
    useGuardianPlayerSessionSummaryQuery(player.id);

  const firstName = player.firstname?.trim() || '';

  const fullName = useMemo(
    () => [player.firstname, player.lastname].filter(Boolean).join(' ').trim() || firstName,
    [player.firstname, player.lastname, firstName],
  );

  const age = useMemo(() => (player as any).age || calculateAge(player.dob), [player]);

  const progressLabel = useMemo(() => {
    const progressData = calculatePlayerProgress(player.level, player.progress);
    if (!progressData) return null;
    return `${progressData.earnedCount}/${progressData.totalCount}`;
  }, [player.level, player.progress]);

  const notesCount = player.notes?.length ?? 0;

  const handleFeatureUnderProgress = useCallback(() => {
    showInfoAlert(STRINGS.common.featureUnderProgress);
  }, []);
  const stats = useMemo(
    () => buildPlayerDetailStats(player, sessionSummary),
    [player, sessionSummary],
  );

  // Pull-to-refresh: refetch profile + session summary together. Spinner state stays in the
  // screen so background invalidations don't leave RefreshControl stuck.
  const refetch = useCallback(async () => {
    await Promise.all([refetchPlayerDetail(), refetchSessionSummary()]);
  }, [refetchPlayerDetail, refetchSessionSummary]);

  return {
    player,
    firstName,
    fullName,
    age,
    progressLabel,
    notesCount,
    stats,
    refetch,
    handleFeatureUnderProgress,
  };
}
